import React from 'react';
import { 
  Drawer, 
  List, 
  ListItemButton,
  ListItemIcon, 
  ListItemText, 
  Divider,
  Box,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { 
  Home as HomeIcon,
  Public as RegionsIcon,
  People as UsersIcon,
  Dashboard as DashboardIcon,
  Grain as GrapeIcon,
  Business as ProducerIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

interface SideNavProps {
  open: boolean;
  onClose?: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
}

const SideNav: React.FC<SideNavProps> = ({ open, onClose, variant }) => {
  const theme = useTheme();
  const location = useLocation();
  const { isSystemAdmin } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  const navItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/admin/dashboard',
      showAlways: true
    },
    { 
      text: 'Wine Regions', 
      icon: <RegionsIcon />, 
      path: '/admin/regions',
      showAlways: true
    },
    { 
      text: 'Producers', 
      icon: <ProducerIcon />, 
      path: '/admin/producers',
      showAlways: true
    },
    { 
      text: 'Grape Varieties', 
      icon: <GrapeIcon />, 
      path: '/admin/grapes',
      showAlways: true
    },
    { 
      text: 'Admin Users', 
      icon: <UsersIcon />, 
      path: '/admin/users',
      showAlways: isSystemAdmin()
    }
  ];
  
  const filteredNavItems = navItems.filter(item => item.showAlways);

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth - 32, // Account for margin
          boxSizing: 'border-box',
          border: 'none',
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          position: 'static',
          height: 'calc(100vh - 32px)', // Account for margin
          borderRadius: 2,
          m: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Box sx={{ 
        p: 3, 
        pt: 2,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-start',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        minHeight: 80,
      }}>
        <img 
          src="/assets/logo-nota-wine.svg" 
          alt="Nota Wine" 
          style={{ 
            height: '48px',
            width: 'auto',
            maxWidth: '100%',
          }}
        />
      </Box>
      
      <List sx={{ px: 2, py: 3 }}>
        {filteredNavItems.map((item, index) => (
          <ListItemButton 
            component={Link as React.ElementType} 
            to={item.path} 
            key={item.text}
            selected={isActive(item.path)}
            sx={{
              mb: 1,
              borderRadius: 2,
              py: 1.25,
              px: 2,
              minHeight: 48,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: 'translateX(4px)',
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
                '& .MuiListItemIcon-root': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: 40,
              color: isActive(item.path) 
                ? theme.palette.primary.main 
                : theme.palette.text.secondary,
              transition: 'color 0.2s ease-in-out',
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive(item.path) ? 600 : 500,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );
};

export default SideNav;
